import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useSocket } from 'use-socketio';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import Board from '../components/game/Board';
import Sidebar from '../components/game/Sidebar';

import BiddingPanel from '../components/game/BiddingPanel';
import DemoBar from '../components/game/DemoBar';
import ScoreBoard from '../components/game/ScoreBoard';
import EventsLog from '../components/game/EventsLog';
import RoomStrip from '../components/game/RoomStrip';
import CelebrationOverlay from '../components/game/CelebrationOverlay';
import MobileHeader from '../components/game/MobileHeader';
import MobileControls from '../components/game/MobileControls';
import MobileTabs from '../components/game/MobileTabs';
import SettingsModal from '../components/game/SettingsModal';
import ModesModal from '../components/game/ModesModal';
import RoomAccessModal from '../components/game/RoomAccessModal';
import { isAnyModalOpen } from '../hooks/useModalEscape';
import useBidEntry, { isTextField, parseBid } from '../hooks/useBidEntry';
import { canRebid, isBidLegal } from '../shared/bidding.mjs';
import { readyToSkipLines } from '../eventLog';
import { useT } from '../i18n';
import { missionLabel, robotColorName } from '../missionLabel';
import ShortcutsModal from '../components/game/ShortcutsModal';
import HamburgerMenu from '../components/game/HamburgerMenu';
import PlayersSheet from '../components/game/PlayersSheet';
import KickedNotice from '../components/game/KickedNotice';
import RoomGate from '../components/game/RoomGate';
import EventsSheet from '../components/game/EventsSheet';

import { assembleBoard } from '../shared/board.mjs';
import useSolutionPlayback from '../hooks/useSolutionPlayback';
import quadrantsData from '../shared/quadrants.json';
import { getReachableTiles, slide } from '../shared/sliding.mjs';
import { logGame, COLOR_NAMES } from '../logging';
import { installDebugHandle } from '../debugHandle';
import useLayoutMode from '../hooks/useLayoutMode';
import { getPlayerId, rotatePlayerId } from '../playerId';
import { loadLog, saveLog } from '../gameLog';
import { shouldConfirmLeave, LEAVE_PROMPT_KEY } from '../leaveGuard';
import { getRoomPassword, setRoomPassword, clearRoomPassword } from '../roomPassword';


const useStyles = createUseStyles({
  wrapper: {
    width: '100%',
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    background: '#1a0f0a',
    overflow: 'hidden',
    overscrollBehaviorX: 'none',
  },
  // Both trees stay mounted and hide each other — `useBidEntry` finds the live
  // bid field by `offsetParent`, so neither may unmount. `layout` comes from
  // useLayoutMode: a media query the player is allowed to overrule.
  mobileContent: {
    display: ({ layout }) => layout === 'mobile' ? 'flex' : 'none',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  boardArea: {
    flex: 1,
    display: 'flex',
    // Centred in both layouts. Mobile used to pin to the top, which collected
    // every pixel the bars did not use into one dead band under the board;
    // the board is square and the bars are all fixed height, so there is
    // nothing left for the slack to be doing down there.
    alignItems: 'center',
    justifyContent: 'center',
    // The desktop frame reads as an object on a table; the mobile one cannot
    // afford that margin, since the bars already own the bottom of the screen.
    // Board.js's `frame` cap subtracts this back out.
    padding: ({ layout }) => layout === 'mobile' ? '8px' : '24px',
    overflow: 'hidden',
  },
  desktopLayout: {
    display: ({ layout }) => layout === 'desktop' ? 'flex' : 'none',
    flex: 1,
    overflow: 'hidden',
  },
});

const FACES = quadrantsData.faces;
const DEFAULT_ARRANGEMENT = ['A', 'B', 'C', 'D'];

function Game() {
  const theme = useTheme();
  const t = useT();
  const { layout, autoLayout, pref: layoutPref, setPref: setLayoutPref } = useLayoutMode();
  const classes = useStyles({ theme, layout });
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { socket } = useSocket();

  // `window.robotrons` for the browser console (dev builds only) — the socket
  // is otherwise unreachable from there. See client/src/debugHandle.js.
  useEffect(() => installDebugHandle(socket), [socket]);

  const {
    phase, boardState, activeMission, bids, scores,
    demoPlayerId, demoMovesRemaining, demoMovesAllowed, biddingTimer, demoTimer,
    players, selfId, hostId, readyToSkip, mode, settings, arrangement, optimalMoves, hint,
  } = useStoreState(s => s.game);

  const {
    addPlayer, removePlayer, updatePlayer, setPhase, setBoardState,
    setActiveMission, setBids, addBid, setScores,
    setDemoPlayerId, setDemoMovesRemaining, setDemoMovesAllowed, setBiddingTimer, setDemoTimer,
    setSelfId, setHostId, setReadyToSkip, applyRobotPosition,
    setMode, setSettings, setArrangement, setOptimalMoves, setHint,
  } = useStoreActions(a => a.game);

  // Local sandbox state (BIDDING only, never sent to server)
  const [sandboxState, setSandboxState] = useState(null);
  const [sandboxSelected, setSandboxSelected] = useState(null);
  const [sandboxMoves, setSandboxMoves] = useState(0);
  const [sandboxHistory, setSandboxHistory] = useState([]);

  // Bid entry. Both bid inputs are mounted at once (the layouts hide each
  // other with display:none), so the value and the keyboard that drives it
  // live here rather than in either panel.
  const [bidValue, setBidValue] = useState('2');
  const bidInputRefs = useRef([React.createRef(), React.createRef()]).current;
  const [desktopBidRef, mobileBidRef] = bidInputRefs;

  // Demo selected robot
  const [demoSelected, setDemoSelected] = useState(null);

  // Robot trails (path lines on the board)
  const [robotTrails, setRobotTrails] = useState({});

  // Nickname (synced with localStorage)
  const [nickname, setNickname] = useState(localStorage.getItem('nickname') ?? '');

  // Event log. Seeded from this tab's copy so a refresh does not hand you an
  // empty panel: nothing on the server records the log, it is composed here
  // from the events as they arrive. See `gameLog.js`.
  const [log, setLog] = useState(() => loadLog(gameId));
  // A key and its params, never a finished sentence: the log is rendered at
  // display time, so switching language retranslates history already on screen.
  const addLog = useCallback((key, params) => {
    setLog(prev => [{ key, params, time: Date.now() }, ...prev].slice(0, 50));
  }, []);
  const clearLog = useCallback(() => setLog([]), []);
  useEffect(() => { saveLog(gameId, log); }, [gameId, log]);

  const [winnerName, setWinnerName] = useState(null);
  const [activeTab, setActiveTab] = useState('game');
  // The events tab carries a dot when something has been logged since it was
  // last looked at. Keyed on the newest entry's timestamp, not on the log's
  // length: `addLog` caps the list at 50, so a length stops moving in a long
  // game and the dot would quietly stop working.
  const [eventsSeenAt, setEventsSeenAt] = useState(() => log[0]?.time ?? 0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [modesOpen, setModesOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [kicked, setKicked] = useState(false);
  // The server refused the join: 'password' or 'not-accepting'. `retry` is what
  // turns "this room needs a password" into "wrong password".
  const [gate, setGate] = useState(null);
  const [roomAccessOpen, setRoomAccessOpen] = useState(false);
  const [access, setAccess] = useState({ visibility: 'public', joinable: true });
  const location = useLocation();

  const isSelfDemo = phase === 'DEMO' && demoPlayerId === selfId;
  const isBidding = phase === 'BIDDING';

  // The events sheet marks the log read while it is open, not only when it is
  // opened — entries that arrive under the reader's eyes are not unread.
  const latestEventAt = log[0]?.time ?? 0;
  useEffect(() => {
    if (activeTab === 'events') setEventsSeenAt(latestEventAt);
  }, [activeTab, latestEventAt]);
  const unreadEvents = latestEventAt > eventsSeenAt;

  // Active robot positions: sandbox during bidding, server state otherwise
  const gamePositions = isBidding && sandboxState ? sandboxState : boardState;

  // Whether the sandbox has solved the current mission
  const sandboxSolved = useMemo(() => {
    if (!sandboxState || !activeMission) return false;
    const { x: tx, y: ty, color: targetColor } = activeMission;
    return Object.entries(sandboxState).some(([color, pos]) => {
      if (targetColor !== 'any' && targetColor !== color) return false;
      return pos.x === tx && pos.y === ty;
    });
  }, [sandboxState, activeMission]);

  // Falls back to the identity arrangement so the board still draws in the
  // moment between mount and the first GAME_STATE.
  const { walls, missions } = useMemo(
    () => assembleBoard(FACES, arrangement ?? DEFAULT_ARRANGEMENT),
    [arrangement]
  );

  // Playing a solver solution out on the board. While it runs it *borrows* the
  // board: its positions and trails outrank the round's, and the round's own
  // state is untouched underneath, so stopping is instant and needs no undo.
  const solutionPlayback = useSolutionPlayback({ walls });

  const activePositions = solutionPlayback.positions ?? gamePositions;
  const robotTrailsShown = solutionPlayback.playing ? solutionPlayback.trails : robotTrails;
  // Nothing is selectable while a solution plays — a highlight ring on a robot
  // the viewer cannot move reads as an invitation, and an arrow key mid-
  // playback would fight the timer for the same position.
  const selectedRobot = solutionPlayback.playing
    ? null
    : (isBidding ? sandboxSelected : (isSelfDemo ? demoSelected : null));

  // Highlighted tiles for selected robot
  const highlightedTiles = useMemo(() => {
    if (!selectedRobot || !activePositions) return [];
    return getReachableTiles(activePositions, walls, selectedRobot);
  }, [selectedRobot, activePositions, walls]);

  // Refs for touch + keyboard handlers (avoid stale closures)
  const isBiddingRef = useRef(isBidding);
  const isSelfDemoRef = useRef(isSelfDemo);
  const sandboxSelectedRef = useRef(sandboxSelected);
  const demoSelectedRef = useRef(demoSelected);
  const activePositionsRef = useRef(activePositions);
  const playersRef = useRef(players);
  const wallsRef = useRef(walls);
  const sandboxUndoRef = useRef(null);
  const demoUndoRef = useRef(null);
  useEffect(() => {
    isBiddingRef.current = isBidding;
    isSelfDemoRef.current = isSelfDemo;
    sandboxSelectedRef.current = sandboxSelected;
    demoSelectedRef.current = demoSelected;
    activePositionsRef.current = activePositions;
    playersRef.current = players;
    wallsRef.current = walls;
    sandboxUndoRef.current = handleSandboxUndo;
    demoUndoRef.current = handleUndo;
  });

  // ── Socket: join on mount ────────────────────────────────────
  // `create` comes from the menu's Create form via router state and is honoured
  // by the server only if this join is what builds the room. Every other route
  // here — a typed code, a shared link, a click in the room list — omits it.
  const joinRoom = useCallback((password) => {
    const username = nickname || 'Player';
    socket.emit('JOIN_GAME', {
      gameId,
      username,
      // Nothing fills this in since Facebook auth was removed; the field
      // stays on the wire so a future provider is a client-only change.
      // See client/docs/firebase-facebook.md.
      avatar: null,
      playerId: getPlayerId(),
      password: password ?? getRoomPassword(gameId) ?? undefined,
      create: location.state?.create,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, nickname, socket]);

  useEffect(() => {
    joinRoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Socket: incoming events
  useSocket('GAME_STATE', ({ phase: p, boardState: bs, activeMission: am, bids: b,
    scores: sc, clients, hostId: hid, selfId: sid, existingClients, readyToSkip: rts,
    demoPlayerId: dpId, demoUsername: dpName, demoMovesRemaining: dmr,
    movesAllowed: dma, biddingTimeRemaining: btr, demoTimeRemaining: dtr,
    mode: m, settings: st, arrangement: arr, optimalMoves: opt, access: acc }) => {
    logGame('GAME_STATE received', { phase: p, players: clients?.length ?? 0 });
    setGate(null);
    if (acc) setAccess(acc);
    setPhase(p);
    if (p === 'LOBBY') { clearLog(); addLog('log.roomInitialized'); setRobotTrails({}); }
    if (m) setMode(m);
    if (st) setSettings(st);
    if (arr) setArrangement(arr);
    setBoardState(bs);
    if (am) setActiveMission(am);
    else if (p === 'LOBBY') setActiveMission(null);
    setBids(b ?? []);
    setScores(sc ?? {});
    if (sid) setSelfId(sid);
    setReadyToSkip(rts ?? []);
    setDemoPlayerId(dpId ?? null);
    setDemoMovesRemaining(dmr ?? 0);
    setDemoMovesAllowed(dma ?? 0);
    setBiddingTimer(btr ?? null);
    setDemoTimer(dtr ?? null);
    setOptimalMoves(opt ?? null);
    // Not carried by the snapshot: a hint is this player's own, and the
    // server-side cursor is what remembers how far they had walked.
    setHint(null);
    if (hid) setHostId(hid);
    else if (clients?.length > 0) setHostId(clients[0].id);
    const allClients = [...(existingClients ?? []), ...(clients ?? [])];
    allClients.forEach(c => addPlayer(c));

    if (p === 'BIDDING' && bs) {
      setSandboxState(JSON.parse(JSON.stringify(bs)));
      setSandboxMoves(0);
      setBidValue('2');
      setSandboxSelected(null);
      setSandboxHistory([]);
      setRobotTrails({});
    }
    if (p === 'DEMO' && dpId) {
      // Which key, not which words: "You are demonstrating" is a decision
      // about the reader, and it is only makeable here, where selfId is known.
      addLog(dpId === sid ? 'log.demoResumeSelf' : 'log.demoResumeOther', {
        name: dpName || t('log.someone'),
        count: dmr ?? 0,
      });
    }
  });

  useSocket('CLIENT_JOINED', payload => {
    logGame('CLIENT_JOINED', payload.username);
    addPlayer(payload);
    addLog('log.joined', { name: payload.username });
  });
  useSocket('CLIENT_LEFT', payload => {
    const leftPlayer = playersRef.current.find(p => p.id === payload.id);
    const name = leftPlayer?.username ?? t('log.aPlayer');
    logGame('CLIENT_LEFT', leftPlayer?.username ?? payload.id, payload.reason ?? 'left');
    removePlayer(payload);
    addLog(payload.reason === 'kicked' ? 'log.removed' : 'log.left', { name });
  });
  useSocket('CLIENT_UPDATED', payload => {
    const oldName = playersRef.current.find(p => p.id === payload.id)?.username;
    updatePlayer(payload);
    if (oldName && oldName !== payload.username) {
      addLog('log.renamed', { from: oldName, to: payload.username });
    }
  });
  useSocket('HOST_CHANGED', ({ hostId: hid }) => {
    if (hid) setHostId(hid);
  });

  // The host removed us. The server has already dropped this socket from the
  // room and will refuse the playerId behind it, so there is nothing to do but
  // say so — KickedNotice's button rotates the id and takes us back out.
  useSocket('KICKED', () => {
    logGame('KICKED');
    setKicked(true);
  });

  useSocket('JOIN_REFUSED', ({ reason }) => {
    logGame('JOIN_REFUSED', { reason });
    // A stored password that no longer works is worse than none — it would
    // re-submit itself forever on every refresh.
    if (reason === 'password') clearRoomPassword(gameId);
    setGate(prev => ({ reason, retry: prev?.reason === 'password' && reason === 'password' }));
  });

  useSocket('ROOM_ACCESS_UPDATED', ({ access: acc }) => {
    setAccess(acc);
    addLog(acc.joinable ? 'log.nowAccepting' : 'log.notAccepting');
  });

  useSocket('SETTINGS_UPDATED', ({ mode: m, settings: st }) => {
    logGame('SETTINGS_UPDATED', m, JSON.stringify(st));
    setMode(m);
    setSettings(st);
    addLog('log.mode', { mode: m });
  });

  // Competitive modes announce the mission's optimum before bidding. The count
  // and nothing else — the server never sends the moves that achieve it.
  useSocket('OPTIMAL_REVEALED', ({ count }) => {
    logGame('OPTIMAL_REVEALED', count);
    setOptimalMoves(count);
    addLog('log.bestPossible', { count });
  });

  // The solution itself, in modes that allow it. Played from `boardState` and
  // never from the sandbox: the server solved the round's opening position, so
  // replaying it against a board someone has been poking at would be a
  // different puzzle with the same answer stapled to it.
  useSocket('SOLVER_SOLUTION', ({ moves, length, error }) => {
    logGame('SOLVER_SOLUTION', error ?? `${length} moves`);
    if (error) {
      addLog('log.noSolution', { error });
      return;
    }
    setSandboxSelected(null);
    setDemoSelected(null);
    solutionPlayback.play(moves, boardState);
    addLog('log.solutionShown', { count: length });
  });

  // Which robot the optimal solution moves next. The colour and the ordinal —
  // the direction is never sent, so this cannot become the solution by
  // repetition the way stepping through the moves would.
  useSocket('HINT_GIVEN', ({ step, total, color, error }) => {
    logGame('HINT_GIVEN', error ?? `${step}/${total}`);
    if (error) {
      addLog('log.noSolution', { error });
      return;
    }
    setHint({ step, total, color });
    addLog('log.hintGiven', { step, color: robotColorName(color, t) });
  });

  const handleChooseMode = useCallback((m, overrides) => {
    socket.emit('CHOOSE_MODE', { mode: m, overrides });
  }, [socket]);

  useSocket('PHASE_CHANGED', ({ phase: p, mission, boardState: bs,
    demoPlayerId: dpId, winnerId, demoUsername, movesAllowed, scores: sc, hostId: hid }) => {
    logGame(`phase -> ${p}`, mission ? `${mission.name} @ (${mission.x},${mission.y})` : '', dpId ?? '', winnerId ?? '');
    setPhase(p);
    if (bs) setBoardState(bs);
    if (mission) setActiveMission(mission);
    if (sc) setScores(sc);
    if (dpId !== undefined) setDemoPlayerId(dpId);
    if (movesAllowed !== undefined) {
      setDemoMovesRemaining(movesAllowed);
      setDemoMovesAllowed(movesAllowed);
    }
    if (hid) setHostId(hid);

    if (p === 'REVEAL' && mission) {
      setBids([]);
      // The next mission's optimum arrives in its own event, and may never
      // arrive at all — clear now so the panel cannot show the last round's.
      setOptimalMoves(null);
      addLog('log.mission', {
        label: missionLabel(mission, t), x: mission.x + 1, y: mission.y + 1,
      });
    }
    if (p === 'DEMO') {
      const name = demoUsername || playersRef.current.find(pl => pl.id === dpId)?.username || t('log.someone');
      addLog('log.demoStart', { name, count: movesAllowed });
    }
    if (p === 'SOLVED') {
      const winner = playersRef.current.find(pl => pl.id === (dpId ?? winnerId))?.username || t('log.someone');
      addLog('log.won', { name: winner });
      setWinnerName(winner);
    }
    if (p === 'FAILED') {
      const failer = playersRef.current.find(pl => pl.id === dpId)?.username || t('log.someone');
      addLog('log.failed', { name: failer });
    }
    if (p === 'SKIP') {
      addLog('log.skipped');
    }
    if (p === 'GAME_OVER') {
      addLog('log.gameOver');
    }

    solutionPlayback.stop();
    setHint(null);

    if (p === 'BIDDING') {
      setRobotTrails({});
      setSandboxState(JSON.parse(JSON.stringify(bs ?? boardState)));
      setSandboxMoves(0);
      setBidValue('2');
      setSandboxSelected(null);
      setSandboxHistory([]);
      addLog('log.biddingOpen');
    }
    if (p !== 'BIDDING') { setSandboxState(null); setBiddingTimer(null); }
    if (p !== 'DEMO') { setDemoSelected(null); setDemoTimer(null); }
    if (p === 'DEMO') setRobotTrails({});
  });

  useSocket('BID_PLACED', payload => {
    logGame(`BID_PLACED ${payload.username} -> ${payload.count}`);
    addBid(payload);
    addLog('log.bid', { name: payload.username, count: payload.count });
  });

  useSocket('TIMER_TICK', ({ seconds }) => setBiddingTimer(seconds));

  useSocket('DEMO_TIMER_TICK', ({ seconds }) => setDemoTimer(seconds));

  useSocket('READY_TO_SKIP_UPDATED', payload => {
    setReadyToSkip(payload.readyPlayerIds);
    // playersRef, not `players` — the handler is registered once and would
    // otherwise close over the player list as it stood on mount.
    readyToSkipLines(payload, playersRef.current).forEach(({ key, params }) => {
      // `name` is itself a key when the player is no longer known — resolve it
      // here, where `t` is in scope, so the entry stores a real name either way.
      const resolved = params?.name?.startsWith?.('log.')
        ? { ...params, name: t(params.name) }
        : params;
      addLog(key, resolved);
    });
  });

  useSocket('ROBOT_MOVED', ({ color, from, to, movesRemaining }) => {
    // Single-robot action mutates the latest board draft — two demo events can
    // arrive before React re-renders (fast input), and a render-scope merge
    // would drop the earlier one, desyncing this tab from the server.
    applyRobotPosition({ color, to });
    logGame(`${COLOR_NAMES[color] ?? color} ${from.x},${from.y} -> ${to.x},${to.y} (${movesRemaining} left)`);
    setDemoMovesRemaining(movesRemaining);
    setRobotTrails(prev => ({
      ...prev,
      [color]: [...(prev[color] || []), { x: from.x, y: from.y }, { x: to.x, y: to.y }],
    }));
  });

  useSocket('MOVE_UNDONE', ({ color, to, movesRemaining }) => {
    applyRobotPosition({ color, to });
    logGame(`undo ${COLOR_NAMES[color] ?? color} -> ${to.x},${to.y} (${movesRemaining} left)`);
    if (movesRemaining !== undefined) setDemoMovesRemaining(movesRemaining);
    setRobotTrails(prev => {
      const trail = prev[color] || [];
      return { ...prev, [color]: trail.slice(0, -2) };
    });
  });

  useSocket('SCORES_UPDATED', ({ scores: sc }) => setScores(sc));

  useSocket('BOARD_CHANGED', ({ arrangement: arr, boardState: bs }) => {
    setArrangement(arr);
    setBoardState(bs);
    addLog('log.shuffled');
  });

  // ── Board interaction ────────────────────────────────────────
  const handleRobotSelect = useCallback((color) => {
    if (solutionPlayback.playing) return;
    if (isBidding) setSandboxSelected(color);
    else if (isSelfDemo) setDemoSelected(color);
  }, [isBidding, isSelfDemo, solutionPlayback.playing]);

  // Asking is all the client does — who hears the answer is the server's call.
  const handleShowSolution = useCallback(() => {
    socket.emit('REQUEST_SOLUTION');
  }, [socket]);

  const handleHint = useCallback(() => {
    socket.emit('REQUEST_HINT');
  }, [socket]);

  // The same permission the server enforces, so the button is absent rather
  // than disabled where the mode forbids it: a greyed-out "show solution" in a
  // competitive room advertises a door that is not there.
  const canAskSolver = settings?.solutionAccess === 'everyone' || selfId === hostId;
  const helpOpen = settings?.solutionAccess !== 'off' && canAskSolver && isBidding;

  const showSolution = helpOpen ? handleShowSolution : undefined;
  const askHint = helpOpen ? handleHint : undefined;

  const handleTileClick = useCallback((tile) => {
    const hit = highlightedTiles.find(t => t.x === tile.x && t.y === tile.y);
    if (!hit) return;

    if (isBidding && sandboxSelected) {
      const fromPos = sandboxState[sandboxSelected];
      setSandboxHistory(prev => [...prev, sandboxState]);
      setSandboxState(prev => ({ ...prev, [sandboxSelected]: { x: hit.x, y: hit.y } }));
      setSandboxMoves(m => m + 1);
      setRobotTrails(prev => ({
        ...prev,
        [sandboxSelected]: [...(prev[sandboxSelected] || []), { x: fromPos.x, y: fromPos.y }, { x: hit.x, y: hit.y }],
      }));
    } else if (isSelfDemo && demoSelected) {
      socket.emit('MOVE_ROBOT', { color: demoSelected, direction: hit.direction });
    }
  }, [highlightedTiles, isBidding, isSelfDemo, sandboxSelected, sandboxState, demoSelected, socket]);

  const handleSandboxReset = useCallback(() => {
    setSandboxState(JSON.parse(JSON.stringify(boardState)));
    setSandboxMoves(0);
    setSandboxSelected(null);
    setSandboxHistory([]);
    setRobotTrails({});
  }, [boardState]);

  const handleSandboxUndo = useCallback(() => {
    if (sandboxHistory.length === 0) return;
    const prevState = sandboxHistory[sandboxHistory.length - 1];
    for (const color of Object.keys(sandboxState)) {
      if (sandboxState[color].x !== prevState[color].x || sandboxState[color].y !== prevState[color].y) {
        setRobotTrails(prev => {
          const trail = prev[color] || [];
          return { ...prev, [color]: trail.slice(0, -2) };
        });
        break;
      }
    }
    setSandboxState(prevState);
    setSandboxMoves(m => Math.max(0, m - 1));
    setSandboxHistory(h => h.slice(0, -1));
  }, [sandboxHistory, sandboxState]);

  const handleBid = useCallback((count) => {
    socket.emit('PLACE_BID', { count, boardState: sandboxState });
    setSandboxSelected(null);
  }, [socket, sandboxState]);

  // Solving the mission in the sandbox proposes that move count as your bid.
  useEffect(() => {
    if (sandboxMoves >= 2) setBidValue(String(sandboxMoves));
  }, [sandboxMoves]);

  // What the mode lets you do with a bid you have already placed. Both are the
  // shared rules the server enforces, so the UI never offers a dropped bid.
  const standingBid = bids?.find(b => b.playerId === selfId)?.count ?? null;
  const hasBid = standingBid !== null;
  const rebidOpen = canRebid({ bids, playerId: selfId, rebidRule: settings?.rebidRule });
  const bidSeed = sandboxMoves >= 2 ? String(sandboxMoves) : '2';
  const { inputProps: bidInputProps, valid: bidValid, commit: commitBid } = useBidEntry({
    active: isBidding && rebidOpen,
    value: bidValue,
    setValue: setBidValue,
    seed: bidSeed,
    inputRefs: bidInputRefs,
    onCommit: handleBid,
  });

  const canBid = bidValid && isBidLegal({
    bids, playerId: selfId, rebidRule: settings?.rebidRule, count: parseBid(bidValue),
  });

  // The quick-pick lane exists because bidding is a race: a bid should cost no
  // mouse trip. Practice has no race to lose — 600s on the clock, the optimum
  // announced and the solution a press away — so the lane is only a row of
  // plates between the board and the bar, and the numeric field stays for the
  // rare bid somebody actually wants to type.
  const quickPick = mode !== 'practice';

  const handlePickBid = useCallback((n) => setBidValue(String(n)), []);
  const handleMoreBids = useCallback(() => mobileBidRef.current?.focus(), [mobileBidRef]);

  const handleUndo = useCallback(() => {
    socket.emit('UNDO_MOVE');
  }, [socket]);

  const handleGiveUp = useCallback(() => {
    socket.emit('GIVE_UP');
  }, [socket]);

  const handleNicknameSave = useCallback((newName) => {
    setNickname(newName);
    localStorage.setItem('nickname', newName);
    socket.emit('UPDATE_NICKNAME', { username: newName });
  }, [socket]);

  const handleStartGame = useCallback(() => {
    socket.emit('START_GAME');
  }, [socket]);

  const handleReadyToSkip = useCallback(() => {
    socket.emit('READY_TO_SKIP');
  }, [socket]);

  const handleUndoReadyToSkip = useCallback(() => {
    socket.emit('UNDO_READY_TO_SKIP');
  }, [socket]);

  const handleResetGame = useCallback(() => {
    socket.emit('RESET_GAME');
  }, [socket]);

  const handleKickPlayer = useCallback((playerId) => {
    socket.emit('KICK_PLAYER', { playerId });
  }, [socket]);

  const handleApplyRoomAccess = useCallback((next) => {
    socket.emit('SET_ROOM_ACCESS', next);
    setRoomAccessOpen(false);
  }, [socket]);

  const handleGatePassword = useCallback((password) => {
    setRoomPassword(gameId, password);
    joinRoom(password);
  }, [gameId, joinRoom]);

  const handleGateLeave = useCallback(() => {
    clearRoomPassword(gameId);
    navigate('/');
  }, [gameId, navigate]);

  const handleLeaveAfterKick = useCallback(() => {
    // A kicked playerId stays refused for the life of the room; a fresh one is
    // what a deliberate re-entry from the menu is made of.
    rotatePlayerId();
    navigate('/');
  }, [navigate]);

  const handleReshuffleBoard = useCallback(() => {
    socket.emit('RESHUFFLE_BOARD');
  }, [socket]);

  // ── Direction movement (shared: keyboard + swipe) ──────────────
  const handleDirection = useCallback((direction) => {
    const color = isBiddingRef.current ? sandboxSelectedRef.current : (isSelfDemoRef.current ? demoSelectedRef.current : null);
    if (!color) return;

    if (isBiddingRef.current) {
      const result = slide(activePositionsRef.current, wallsRef.current, color, direction);
      const start = activePositionsRef.current[color];
      if (result.x === start.x && result.y === start.y) return;
      setSandboxHistory(prev => [...prev, activePositionsRef.current]);
      setSandboxState(prev => ({ ...prev, [color]: result }));
      setSandboxMoves(m => m + 1);
      setRobotTrails(prev => ({
        ...prev,
        [color]: [...(prev[color] || []), { x: start.x, y: start.y }, { x: result.x, y: result.y }],
      }));
    } else if (isSelfDemoRef.current) {
      socket.emit('MOVE_ROBOT', { color, direction });
    }
  }, [socket]);

  // ── Keyboard arrows ────────────────────────────────────────────────
  useEffect(() => {
    const DIR_MAP = {
      ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
    };

    const handler = (e) => {
      if (e.key === 'Escape') {
        // The modal on top closes itself; the board must not react underneath it.
        if (isAnyModalOpen()) return;
        setSandboxSelected(null);
        setDemoSelected(null);
        return;
      }

      if (e.key === '?') {
        if (isTextField(e.target)) return;
        e.preventDefault();
        setShortcutsOpen(prev => !prev);
        return;
      }

      // Typing a bid (or a nickname) owns the keyboard: arrows move the caret,
      // Ctrl+Z undoes the typing. Neither should reach the board.
      if (isTextField(e.target)) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (isBiddingRef.current) sandboxUndoRef.current();
        else if (isSelfDemoRef.current) demoUndoRef.current();
        return;
      }

      const direction = DIR_MAP[e.key];
      if (!direction) return;

      e.preventDefault();
      handleDirection(direction);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleDirection]);

  // ── Warn before leaving the page ──────────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // ── Leaving on purpose ────────────────────────────────────────────
  // The brand is a plain router <Link>, so it fires no unload and pops no
  // history: neither guard above sees it, and a mis-aimed click on the logo
  // used to walk straight out of a live round. Both layouts hang their brand
  // on this one handler.
  const handleLeaveGame = useCallback((e) => {
    if (shouldConfirmLeave(phase) && !window.confirm(t(LEAVE_PROMPT_KEY))) e.preventDefault();
    // `t` changes identity with the locale — without it here, switching
    // language mid-round would leave this prompt in the previous one.
  }, [phase, t]);

  // ── Block swipe-back / hardware back navigation ───────────────────
  useEffect(() => {
    window.history.pushState(null, '');

    const handlePopState = () => {
      window.history.pushState(null, '');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // ── Touch swipe on board ──────────────────────────────────────────
  const touchStart = useRef(null);

  const handleBoardTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleBoardTouchMove = useCallback((e) => {
    if (!touchStart.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      const hasSelected = isBiddingRef.current ? sandboxSelectedRef.current : (isSelfDemoRef.current ? demoSelectedRef.current : null);
      if (hasSelected) e.preventDefault();
    }
  }, []);

  const handleBoardTouchEnd = useCallback((e) => {
    if (!touchStart.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    touchStart.current = null;

    if (Math.abs(dx) < 20 && Math.abs(dy) < 20) return;

    const direction = Math.abs(dx) > Math.abs(dy)
      ? (dx > 0 ? 'right' : 'left')
      : (dy > 0 ? 'down' : 'up');
    handleDirection(direction);
  }, [handleDirection]);

  return (
    <div className={classes.wrapper}>
      {/* Mobile layout: shown when useLayoutMode resolves to 'mobile' */}
      <div className={classes.mobileContent}>
        <MobileHeader
          players={players}
          selfId={selfId}
          nickname={nickname}
          onMenuOpen={() => setMenuOpen(true)}
          onNicknameClick={() => setSettingsOpen(true)}
          onLeave={handleLeaveGame}
        />

        <div className={classes.boardArea}
          onTouchStart={handleBoardTouchStart}
          onTouchMove={handleBoardTouchMove}
          onTouchEnd={handleBoardTouchEnd}
        >
          <Board
            boardState={activePositions}
            walls={walls}
            missions={missions}
            highlightedTiles={highlightedTiles}
            selectedRobot={selectedRobot}
            activeMission={activeMission}
            robotTrails={robotTrailsShown}
            onRobotSelect={handleRobotSelect}
            onTileClick={handleTileClick}
            compact
          />
        </div>

        <MobileControls
          phase={phase}
          sandboxMoves={sandboxMoves}
          isSelfDemo={isSelfDemo}
          demoMovesRemaining={demoMovesRemaining}
          demoPlayerId={demoPlayerId}
          players={players}
          mode={mode}
          bids={bids}
          biddingTimer={biddingTimer}
          demoTimer={demoTimer}
          optimalMoves={optimalMoves}
          onUndo={isBidding ? handleSandboxUndo : handleUndo}
          onReset={handleSandboxReset}
          onBid={commitBid}
          hasBid={hasBid}
          rebidOpen={rebidOpen}
          canBid={canBid}
          standingBid={standingBid}
          bidValue={bidValue}
          bidInputProps={bidInputProps}
          bidInputRef={mobileBidRef}
          onPickBid={handlePickBid}
          onMoreBids={handleMoreBids}
          quickPick={quickPick}
          onShowSolution={showSolution}
          onHint={askHint}
          hint={hint}
          onStopSolution={solutionPlayback.stop}
          solutionProgress={solutionPlayback.progress}
          onGiveUp={handleGiveUp}
          onReadyToSkip={handleReadyToSkip}
          onUndoReadyToSkip={handleUndoReadyToSkip}
          onStartGame={handleStartGame}
          onReshuffleBoard={handleReshuffleBoard}
          isHost={selfId === hostId}
          iAmReady={readyToSkip?.includes(selfId)}
          everyoneReady={readyToSkip?.length >= (players?.length ?? 0)}
        />

        <MobileTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          playerCount={players?.length ?? 0}
          unreadEvents={unreadEvents}
        />

        <PlayersSheet
          open={activeTab === 'players'}
          onClose={() => setActiveTab('game')}
          players={players}
          scores={scores}
          selfId={selfId}
          hostId={hostId}
          onKickPlayer={handleKickPlayer}
          phase={phase}
          bids={bids}
          readyToSkip={readyToSkip}
          demoPlayerId={demoPlayerId}
          gameId={gameId}
        />

        <EventsSheet
          open={activeTab === 'events'}
          onClose={() => setActiveTab('game')}
          log={log}
        />

        <HamburgerMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          isHost={selfId === hostId}
          onResetGame={handleResetGame}
          onOpenShortcuts={() => setShortcutsOpen(true)}
          mode={mode}
          onOpenSettings={() => setSettingsOpen(true)}
        />

      </div>

      {/* Desktop layout: shown when useLayoutMode resolves to 'desktop' */}
      <div className={classes.desktopLayout}>
        <div className={classes.boardArea}
          onTouchStart={handleBoardTouchStart}
          onTouchMove={handleBoardTouchMove}
          onTouchEnd={handleBoardTouchEnd}
        >
          <Board
            boardState={activePositions}
            walls={walls}
            missions={missions}
            highlightedTiles={highlightedTiles}
            selectedRobot={selectedRobot}
            activeMission={activeMission}
            robotTrails={robotTrailsShown}
            onRobotSelect={handleRobotSelect}
            onTileClick={handleTileClick}
          />
        </div>

        <Sidebar
          phase={phase}
          selfId={selfId}
          hostId={hostId}
          onStartGame={handleStartGame}
          onReshuffleBoard={handleReshuffleBoard}
          onLeave={handleLeaveGame}
        >
          {isBidding && (
            <BiddingPanel
              bids={bids}
              timerSeconds={biddingTimer}
              readyToSkip={readyToSkip}
              players={players}
              selfId={selfId}
              onReadyToSkip={handleReadyToSkip}
              onUndoReadyToSkip={handleUndoReadyToSkip}
              sandboxMoves={sandboxMoves}
              solved={sandboxSolved}
              onUndo={handleSandboxUndo}
              onReset={handleSandboxReset}
              onBid={commitBid}
              optimalMoves={optimalMoves}
              hasBid={hasBid}
              rebidOpen={rebidOpen}
              canBid={canBid}
              bidInputProps={bidInputProps}
              bidInputRef={desktopBidRef}
              onShowSolution={showSolution}
              onHint={askHint}
              hint={hint}
              onStopSolution={solutionPlayback.stop}
              solutionProgress={solutionPlayback.progress}
            />
          )}
          {phase === 'DEMO' && (
            <DemoBar
              demoPlayerId={demoPlayerId}
              players={players}
              movesRemaining={demoMovesRemaining}
              movesAllowed={demoMovesAllowed}
              timerSeconds={demoTimer}
              isSelfDemo={isSelfDemo}
              onUndo={handleUndo}
              onGiveUp={handleGiveUp}
            />
          )}
          <RoomStrip
            gameId={gameId}
            mode={mode}
            access={access}
            onSettings={() => setSettingsOpen(true)}
          />
          <ScoreBoard
            players={players}
            scores={scores}
            selfId={selfId}
            hostId={hostId}
            demoPlayerId={demoPlayerId}
            bids={bids}
            phase={phase}
            onKickPlayer={handleKickPlayer}
          />
          <EventsLog log={log} />
        </Sidebar>
      </div>

      <SettingsModal
        open={settingsOpen}
        onClose={() => { setSettingsOpen(false); setModesOpen(false); }}
        nickname={nickname}
        onNicknameChange={handleNicknameSave}
        layoutPref={layoutPref}
        autoLayout={autoLayout}
        onLayoutPrefChange={setLayoutPref}
        isHost={selfId === hostId}
        onResetGame={handleResetGame}
        onOpenShortcuts={() => setShortcutsOpen(true)}
        mode={mode}
        settings={settings}
        onOpenModes={() => setModesOpen(true)}
        access={access}
        onOpenRoomAccess={() => setRoomAccessOpen(true)}
      />

      <RoomAccessModal
        open={roomAccessOpen}
        access={access}
        isHost={selfId === hostId}
        onApply={handleApplyRoomAccess}
        onClose={() => setRoomAccessOpen(false)}
      />

      <ModesModal
        open={modesOpen}
        onClose={() => setModesOpen(false)}
        mode={mode}
        settings={settings}
        isHost={selfId === hostId}
        phase={phase}
        onChoose={handleChooseMode}
      />

      <ShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {winnerName && (
        <CelebrationOverlay username={winnerName} onDone={() => setWinnerName(null)} />
      )}

      {kicked && <KickedNotice onLeave={handleLeaveAfterKick} />}
      {!kicked && gate && (
        <RoomGate
          reason={gate.reason}
          retry={gate.retry}
          onSubmit={handleGatePassword}
          onLeave={handleGateLeave}
        />
      )}
    </div>
  );
}

export default Game;
